import { useSyncExternalStore } from 'react';

/**
 * MUI's own persistence key.
 *
 * With `cssVariables`, MUI's ThemeProvider is a colour-scheme provider: it reads
 * this key on mount and re-stamps `data-mui-color-scheme` from it. Without
 * keeping it in step, our attribute survives only until React mounts — which is
 * why a chosen dark theme used to silently revert to light on every reload.
 * `fms.scheme` stays the source of truth; this is a mirror written alongside it.
 */
const MUI_MODE_KEY = 'mui-mode';

/**
 * Pre-hydration bootstrap. Inlined into index.html as a plain string so the
 * correct color scheme / contrast / direction / language are stamped on <html>
 * BEFORE first paint — zero theme flash, zero CLS.
 */
export const THEME_INIT_SCRIPT = `(function () {
  try {
    var ls = window.localStorage;
    var scheme = ls.getItem('fms.scheme');
    if (scheme !== 'light' && scheme !== 'dark') {
      scheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    var contrast = ls.getItem('fms.contrast') === 'high' ||
      window.matchMedia('(prefers-contrast: more)').matches ? 'high' : 'normal';
    var lang = ls.getItem('fms.lang') || 'en';
    var root = document.documentElement;
    root.setAttribute('data-mui-color-scheme', scheme);
    // Seed MUI's own key so its provider hydrates to the same scheme instead of
    // re-stamping the attribute to its default on mount.
    if (ls.getItem('mui-mode') !== scheme) ls.setItem('mui-mode', scheme);
    if (contrast === 'high') root.setAttribute('data-contrast', 'high');
    root.setAttribute('lang', lang);
    root.setAttribute('dir', 'ltr');
  } catch (e) { /* first paint falls back to light */ }
})();`;

export type ColorScheme = 'light' | 'dark';

export function applyScheme(scheme: ColorScheme): void {
  document.documentElement.setAttribute('data-mui-color-scheme', scheme);
  window.localStorage.setItem('fms.scheme', scheme);
  window.localStorage.setItem(MUI_MODE_KEY, scheme);
}

export function applyContrast(high: boolean): void {
  if (high) document.documentElement.setAttribute('data-contrast', 'high');
  else document.documentElement.removeAttribute('data-contrast');
  window.localStorage.setItem('fms.contrast', high ? 'high' : 'normal');
}

export function applyLanguage(lang: string): void {
  document.documentElement.setAttribute('lang', lang);
  window.localStorage.setItem('fms.lang', lang);
}

export function currentScheme(): ColorScheme {
  return document.documentElement.getAttribute('data-mui-color-scheme') === 'dark'
    ? 'dark'
    : 'light';
}

export function isHighContrast(): boolean {
  return document.documentElement.getAttribute('data-contrast') === 'high';
}

/**
 * Reactive read of the active scheme.
 *
 * `<html data-mui-color-scheme>` is the single source of truth — the
 * pre-hydration script stamps it and `applyScheme` flips it — but an attribute
 * is not React state, so components that must *branch on* the scheme (rather
 * than just style off a CSS variable) have nothing to re-render against. This
 * subscribes to that one attribute so a theme toggle anywhere updates them,
 * without every caller having to thread the value down or duplicate the
 * `useState(currentScheme)` pattern.
 *
 * Prefer a scheme-aware CSS variable when you only need a colour; reach for this
 * when the choice changes what is rendered — picking the dark-ink logo, say.
 */
export function useScheme(): ColorScheme {
  return useSyncExternalStore(subscribeToScheme, currentScheme, () => 'light' as ColorScheme);
}

function subscribeToScheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-mui-color-scheme'],
  });
  return () => observer.disconnect();
}
